// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useTranslation } from 'react-i18next';
import { ResponsiveRadar } from '@nivo/radar';
import { type Factors as FactorAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { ChartContainer } from '@shared/ui-kit/components';
import { useMemo } from 'react';

interface FactorsChartProps {
  factors: FactorAnalysis[];
}

export const FactorsChart = ({ factors }: FactorsChartProps): JSX.Element => {
  const { t } = useTranslation();
  const data = useMemo(
    () => factors.map(factor => ({ name: factor.name, score: factor.value })),
    [factors],
  );

  return (
    <ChartContainer
      header={t('common.factors', { ns: 'common' })}
      popupName="charts.factors"
    >
      <ResponsiveRadar
        data={data}
        indexBy={'name'}
        keys={['score']}
        valueFormat=" >-.2%"
        colors={'var(--primaryColor)'}
        fillOpacity={0.5}
        borderWidth={2}
        borderColor="#1072BA"
        dotColor="var(--color-positive)"
        dotSize={7}
        margin={{ top: 40, right: 60, bottom: 40, left: 100 }}
        gridLabelOffset={20}
        maxValue={1}
        theme={{
          axis: {
            ticks: {
              text: {
                fontSize: 12,
                fontFamily: 'poppins',
                fill: 'var(--blueGrayMuted)',
                textTransform: 'uppercase',
              },
            },
          },
        }}
        animate={false}
      />
    </ChartContainer>
  );
};
